import { observer } from "mobx-react";
import {
  AdminElectronicGeneralMeetingController,
  AttendanceData,
  CopyNoticeUrlForShareholderForm,
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  headerActions,
  isGivePowerOfAttorneyToRoleDisabled,
  isShareholderAttending,
  LiveVotingResults,
  MeetingAnnouncementForm,
  MeetingAttendanceDialogState,
  MeetingDialogState,
  MeetingEndForm,
  MeetingResendNoticeForm,
  MeetingSettingsForm,
  MeetingStartForm,
  RegisterPowerOfAttorneyForm,
  StatusName,
  VotesData,
} from "./electronicGeneralMeetingDomain";
import { FC, useEffect } from "react";
import {
  decisionLabel,
  GeneralMeetingVoteType,
  informationMatterLabel,
  registrationLabel,
} from "./generalMeetingDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { TMessage } from "@/i18n/tt";
import {
  GeneralMeeting,
  GivePowerOfAttorneyToRole,
  HeaderRowInMeetingTab,
} from "./generalMeetingComponents";
import { Tag } from "@/components-v2/Tag";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Box, VBox } from "@/components-v2/Box";
import {
  DbotEvent,
  DbotEventDispatcher,
  GeneralMeetingIndividualVote,
  GeneralMeetingParticipation,
  ProjectContext,
} from "@/dbot/common/commonDomain";
import { ProgressBar } from "@/components-v2/ProgressBar";
import { Popover } from "@/components-v2/Popover";
import { percentOf } from "@/utils/numbers";
import { User } from "@/types/UserInfo";
import {
  GeneralMeetingVoting,
  GeneralMeetingVotingType,
} from "@/dbot/common/commonGeneralMeetingDomain";
import { AttendanceDialog } from "./AdminElectronicGeneralMeetingAttendanceDialog";
import { VotingDialog } from "./AdminElectronicGeneralMeetingVotingDialog";
import {
  VotingOutcomeSettings,
  VotingTypeSettings,
} from "@/dbot/common/commonGeneralMeetingComponents";
import { Dialog } from "@/components-v2/Dialog";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { Markdown } from "@/text/Markdown";
import { MTextInput } from "@/components-v2/TextInput";
import { isFailure, isOk, isPending } from "@/common/common";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { formatDate } from "@/i18n/dateFormat";
import { GeneralMeetingMinutesSigners } from "@/dbot/generalMeetingMinutes/generalMeetingMinutesComponents";
import { TMessageKey } from "@/i18n/types.gen";
import {
  useDispatch,
  useLanguage,
  useLocaleFormat,
  useTranslation,
} from "@/stores/RootStoreContext";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { GeneralMeetingVotingModel } from "@/dbot/generalMeetingMinutes/generalMeetingMinutesDomain";
import { Icon } from "@/components-v2/Icon";
import { showError, showSuccess } from "@/events/eventsDomain";

const AdminElectronicGeneralMeetingInitializer: FC<{
  projectId: string;
  controller: AdminElectronicGeneralMeetingController;
}> = observer(({ projectId: projectId, controller }) => {
  const { t } = useTranslation();
  const result = controller.noticeControllerResult;

  useEffect(() => {
    controller.fetchMeetingPreview(projectId);
  }, [projectId, controller]);

  switch (result.state) {
    case "pending":
      return <LoadingIndicator />;
    case "failure":
      return <Alert type={"error"} message={t(result.error)} />;
    case "cancelled":
      return <div />;
    case "ok":
      return (
        <VBox gap={"md"}>
          <HeaderRowInMeetingTab
            isScreenShareMode={controller.isScreenShareMode}
            {...headerActions(controller)}
          />
          <GeneralMeeting
            data={result.data.response}
            selectedAttendance={result.data.selectedAttendance}
            onDownloadAttachment={result.data.downloadNoticeToGeneralMeetingAttachment}
            agendaItem={controller.currentAgendaItem}
            openAgendaItem={controller.setCurrentAgendaItem}
            isPreview
          />
        </VBox>
      );
  }
});

const renderStatusTag = (status?: GeneralMeetingStatus) => {
  switch (status) {
    case undefined:
      return <Tag label={{ key: "not-started" }} color={"neutral"} />;
    case GeneralMeetingStatus.FINISHED:
      return <Tag label={{ key: "done" }} color={"success"} />;
    case GeneralMeetingStatus.STARTED:
      return <Tag label={{ key: "opened" }} color={"info"} />;
  }
};

export const GeneralMeetingStatusTag: FC<{ status?: GeneralMeetingStatus }> = observer(
  ({ status }) => {
    return <div>{renderStatusTag(status)}</div>;
  },
);

export const AttendanceRegistration: FC<{
  projectId: string;
  meeting: ElectronicGeneralMeeting;
  context: ProjectContext;
  dispatch: DbotEventDispatcher;
  data: AttendanceData;
  controller: AdminElectronicGeneralMeetingController;
  isNoticeSent?: boolean;
  next?: DbotEvent[];
}> = observer(({ projectId, meeting, dispatch, data, context, controller, isNoticeSent, next }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();

  const isNoticeSentOrLive = isNoticeSent || meeting.status === GeneralMeetingStatus.STARTED;

  const votesPercent = percentOf(data.numberOfVotesAttending, data.totalNumberOfVotes);

  return (
    <VBox>
      {renderStatusTag(meeting?.registrationStatus)}
      <VBox marginBottom={"lg"}>
        <Box alignItems="center" testId={"register-number"}>
          <b>{t("general-meeting.register.number")}</b>
          {[
            formatNumber(data.numberOfAttendingShareholders),
            formatNumber(data.numberOfVotesAttending),
            formatNumber(data.attendingShareCapital, { maximumFractionDigits: 2 }),
          ].join(" / ")}
          <Popover
            bg={"dark"}
            button={<IconButton icon={"info"} tooltip={""} />}
            content={
              <VBox>
                <strong>
                  {t("documents.attendingShareholders.represented")} {formatPercent(votesPercent)}
                </strong>
                <Box justifyContent={"space-between"}>
                  <div>{t("votes")}</div>
                  <div>{formatNumber(data.numberOfVotesAttending)}</div>
                </Box>
                <Box justifyContent={"space-between"}>
                  <div>{t("header.shareholders")}</div>
                  <div>{formatNumber(data.numberOfAttendingShareholders)}</div>
                </Box>
                <Box justifyContent={"space-between"}>
                  <div>{t("shareClasses.capital")}</div>
                  <div>
                    {formatNumber(data.attendingShareCapital, { maximumFractionDigits: 2 })}
                  </div>
                </Box>
              </VBox>
            }
          />
        </Box>
        <ProgressBar value={votesPercent} />
        <Box fg={"subtle"}>
          {t("general-meeting-attendance-of-total", {
            shareholders: formatNumber(data.totalNumberOfShareholders),
            votes: formatNumber(data.totalNumberOfVotes),
            totalSharecapital: formatNumber(data.totalShareCapital, { maximumFractionDigits: 2 }),
          })}
        </Box>
      </VBox>
      <VBox gap={"md"} marginBottom={"lg"}>
        <Button
          icon={
            meeting?.registrationStatus === GeneralMeetingStatus.FINISHED
              ? "arrow-counter-clockwise"
              : undefined
          }
          variant={
            meeting?.registrationStatus === GeneralMeetingStatus.FINISHED ? "secondary" : "primary"
          }
          label={t(registrationLabel(meeting?.registrationStatus))}
          fullWidth
          onClick={() =>
            controller.updateMeetingStatus({
              statusName: StatusName.REGISTRATION,
              meetingStatus: meeting.registrationStatus,
              participationList: meeting.participationList,
            })
          }
          disabled={!isNoticeSentOrLive}
        />

        <Button
          icon={"table"}
          label={t("general-meeting.attendance.showRegistrationDialog")}
          variant={"secondary"}
          onClick={() => controller.setShowGeneralMeetingDialogs()}
          disabled={!isNoticeSentOrLive}
        />

        <Button
          icon={"file-plus"}
          label={t("general-meeting.attendance.powerOfAttorney.register")}
          variant={"secondary"}
          onClick={() => controller.openRegisterPowerOfAttorneyDialog()}
          disabled={!isNoticeSentOrLive}
        />
      </VBox>

      <strong>{t("general-meeting.attendance.options")}</strong>
      <Checkbox
        label={t("general-meeting.attendance.include-participants-in-minutes")}
        disabled={
          controller.projectContext.generalMeetingFields?.includeListOfParticipantsInMinutes
            .value === undefined
        }
        checked={
          controller.projectContext.generalMeetingFields?.includeListOfParticipantsInMinutes
            .value ?? false
        }
        onChange={() => controller.toggleIncludeListOfParticipantsInMinutes()}
      />
      {controller.projectContext.generalMeetingFields.includeListOfParticipantsInMinutes.error && (
        <Alert
          type="error"
          message={
            <>
              {t(
                controller.projectContext.generalMeetingFields.includeListOfParticipantsInMinutes
                  .error,
              )}
            </>
          }
        />
      )}
      {controller.showRegistrationDialog && (
        <AttendanceDialog
          projectId={projectId}
          context={context}
          controller={controller}
          registrationStatus={meeting?.registrationStatus}
          data={data}
          meeting={meeting}
          dispatch={dispatch}
          next={next}
        />
      )}
    </VBox>
  );
});

const VotingCharts: FC<{
  data: LiveVotingResults;
  votingType?: GeneralMeetingVotingType;
}> = observer(({ data, votingType }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <>
      <VotingChart
        data={data}
        votes={data.votesFor}
        requiredValue={
          votingType === GeneralMeetingVotingType.QUALIFIED ? percentOf(2, 3) : percentOf(1, 2)
        }
        votingType={votingType}
        labelHeader={"documents.generalMeetingVoting.voteFor"}
        labelVotedFor={"n-voted-for"}
      />
      <VotingChart
        data={data}
        votes={data.votesAgainst}
        votingType={votingType}
        labelHeader={"documents.generalMeetingVoting.voteAgainst"}
        labelVotedFor={"n-voted-against"}
      />
      <Box fg={"subtle"}>
        {t("general-meeting-voting-of-total", {
          votes: formatNumber(data.representedVotes),
          shareholders: formatNumber(data.representedShareholders),
          totalSharecapital: formatNumber(data.representedShareCapital, {
            maximumFractionDigits: 2,
          }),
        })}
      </Box>
    </>
  );
});

const VotingChart: FC<{
  data: LiveVotingResults;
  votes: VotesData;
  requiredValue?: number;
  votingType?: GeneralMeetingVotingType;
  labelHeader: TMessageKey;
  labelVotedFor: TMessageKey;
}> = observer(({ data, votes, requiredValue, votingType, labelHeader, labelVotedFor }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();

  return (
    <>
      <Box alignItems="center" testId={"generalMeetingVoting"}>
        <b>{t(labelHeader)}: </b>

        {[
          formatNumber(votes.numberOfVotes),
          formatNumber(votes.numberOfShareholders),
          formatNumber(votes.sharecapital, { maximumFractionDigits: 2 }),
        ].join(" / ")}

        <Popover
          bg={"dark"}
          button={<IconButton icon={"info"} tooltip={""} />}
          content={
            <VBox>
              <strong>
                {t(labelVotedFor, {
                  n: formatPercent(
                    percentOf(
                      votes.numberOfVotes,
                      votingType === GeneralMeetingVotingType.UNANIMOUS
                        ? data.representedVotes
                        : data.votesFor.numberOfVotes + data.votesAgainst.numberOfVotes,
                    ),
                  ),
                })}
              </strong>
              <Box justifyContent={"space-between"}>
                <div>{t("votes")}</div>
                <div>{formatNumber(votes.numberOfVotes)}</div>
              </Box>
              <Box justifyContent={"space-between"}>
                <div>{t("header.shareholders")}</div>
                <div>{formatNumber(votes.numberOfShareholders)}</div>
              </Box>
              <Box justifyContent={"space-between"}>
                <div>{t("shareClasses.capital")}</div>
                <div>{formatNumber(votes.sharecapital, { maximumFractionDigits: 2 })}</div>
              </Box>
            </VBox>
          }
        />
      </Box>
      <VBox>
        <Box alignItems={"center"}>
          <Icon name={"hand"} />
          <ProgressBar
            value={percentOf(
              votes.numberOfVotes,
              votingType === GeneralMeetingVotingType.UNANIMOUS
                ? data.representedVotes
                : data.votesFor.numberOfVotes + data.votesAgainst.numberOfVotes,
            )}
            secondaryValue={
              votingType === GeneralMeetingVotingType.UNANIMOUS ? undefined : requiredValue
            }
          />
        </Box>
        {votingType === GeneralMeetingVotingType.QUALIFIED && (
          <Box alignItems={"center"}>
            <Icon name={"currency-circle-dollar"} />
            <ProgressBar
              value={percentOf(votes.sharecapital, data.representedShareCapital)}
              secondaryValue={requiredValue}
            />
          </Box>
        )}
      </VBox>
    </>
  );
});

const MatterVotingStatus = observer(function MatterVotingStatus({
  status,
  votes,
  participants,
  defaultVotingType,
  isApproved,
  votesModel,
  onSaveVotingType,
}: {
  status?: GeneralMeetingStatus;
  votes?: GeneralMeetingIndividualVote[];
  participants?: GeneralMeetingParticipation[];
  defaultVotingType: GeneralMeetingVotingType;
  isApproved?: boolean;
  votesModel?: GeneralMeetingVotingModel;
  onSaveVotingType?: (votingType?: GeneralMeetingVotingType) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"space-between"}>
      <Box alignItems={"center"}>
        {renderStatusTag(status)}
        {status !== undefined && (
          <span>
            {t("general-meeting.number-of-participants-voted", {
              votedCount: votes?.length ?? 0,
              participantCount: participants?.filter((x) => x.isPresent).length ?? 0,
            })}
          </span>
        )}
      </Box>
      {votesModel && (
        <VotingTypeSettings
          defaultVotingType={defaultVotingType}
          isApproved={isApproved}
          isDisabled={status !== undefined}
          isVotesRegistered={
            votesModel.type === "live" &&
            votesModel.isVotesRegistered &&
            ((votes?.length ?? 0) > 0 || status !== undefined)
          }
          voting={votesModel}
          onSaveVotingType={onSaveVotingType}
        />
      )}
    </Box>
  );
});

export const ChoiceOfMeetingChair: FC<{
  projectId: string;
  meeting: ElectronicGeneralMeeting;
  dispatch: DbotEventDispatcher;
  data: LiveVotingResults;
  controller: AdminElectronicGeneralMeetingController;
  user: User;
  onSaveVoting: (voting: GeneralMeetingVoting) => void;
  isNoticeSent?: boolean;
  next?: DbotEvent[];
  prev?: DbotEvent[];
}> = observer(
  ({
    projectId,
    meeting,
    dispatch,
    data,
    controller,
    onSaveVoting,
    user,
    isNoticeSent,
    next,
    prev,
  }) => {
    const { t } = useTranslation();
    const isNoticeSentOrLive = isNoticeSent || meeting.status === GeneralMeetingStatus.STARTED;
    return (
      <VBox>
        <MatterVotingStatus
          status={meeting?.meetingChairStatus}
          votes={meeting?.votes?.meetChair}
          participants={meeting?.participationList}
          defaultVotingType={GeneralMeetingVotingType.SIMPLE_MAJORITY}
        />
        <GeneralMeetingMinutesSigners
          context={controller.projectContext}
          newSigner={controller.newSignerForm}
          openNewSigner={controller.openNewSigner}
        />
        <VotingCharts data={data} votingType={GeneralMeetingVotingType.SIMPLE_MAJORITY} />
        <VBox gap={"md"} marginTop={"md"} marginBottom={"md"}>
          <Button
            icon={
              meeting?.meetingChairStatus === GeneralMeetingStatus.FINISHED
                ? "arrow-counter-clockwise"
                : undefined
            }
            variant={
              meeting?.meetingChairStatus === GeneralMeetingStatus.FINISHED
                ? "secondary"
                : "primary"
            }
            label={t(decisionLabel(meeting?.meetingChairStatus))}
            fullWidth
            onClick={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.MEETING_CHAIR,
                meetingStatus: meeting?.meetingChairStatus,
              })
            }
            disabled={!isNoticeSentOrLive}
          />
          <Button
            label={t("register-votes-manually")}
            variant={"secondary"}
            onClick={() =>
              controller.setShowGeneralMeetingDialogs(GeneralMeetingVoteType.meetChair)
            }
            icon={"table"}
            disabled={!isNoticeSentOrLive}
          />
        </VBox>
        {controller.getShowVotingDialogForType(GeneralMeetingVoteType.meetChair) && (
          <VotingDialog
            title={{ key: "documents.generalMeeting.choiceOfMeetingChair" }}
            projectId={projectId}
            controller={controller}
            meeting={meeting}
            user={user}
            data={data}
            dispatch={dispatch}
            onUpdateVotingStatus={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.MEETING_CHAIR,
                meetingStatus: meeting?.meetingChairStatus,
              })
            }
            next={next}
            prev={prev}
          />
        )}
        {controller.votesModel && (
          <VotingOutcomeSettings
            model={controller.votesModel.meetChair}
            data={data}
            isDisabled={!isNoticeSentOrLive}
            onChange={onSaveVoting}
          />
        )}
      </VBox>
    );
  },
);

export const ApproveAgenda: FC<{
  projectId: string;
  meeting: ElectronicGeneralMeeting;
  dispatch: DbotEventDispatcher;
  data: LiveVotingResults;
  controller: AdminElectronicGeneralMeetingController;
  user: User;
  onSaveVoting: (voting: GeneralMeetingVoting, decisionId?: string) => void;
  isNoticeSent?: boolean;
  next?: DbotEvent[];
  prev?: DbotEvent[];
}> = observer(
  ({
    data,
    projectId,
    meeting,
    dispatch,
    controller,
    user,
    onSaveVoting,
    isNoticeSent,
    next,
    prev,
  }) => {
    const { t } = useTranslation();
    const isNoticeSentOrLive = isNoticeSent || meeting.status === GeneralMeetingStatus.STARTED;

    return (
      <VBox>
        <MatterVotingStatus
          status={meeting?.approveAgendaStatus}
          votes={meeting?.votes?.approveNoticeAndAgenda}
          participants={meeting?.participationList}
          defaultVotingType={GeneralMeetingVotingType.SIMPLE_MAJORITY}
        />
        <VotingCharts data={data} votingType={GeneralMeetingVotingType.SIMPLE_MAJORITY} />
        <VBox gap={"md"} marginTop={"md"} marginBottom={"md"}>
          <Button
            icon={
              meeting?.approveAgendaStatus === GeneralMeetingStatus.FINISHED
                ? "arrow-counter-clockwise"
                : undefined
            }
            variant={
              meeting?.approveAgendaStatus === GeneralMeetingStatus.FINISHED
                ? "secondary"
                : "primary"
            }
            label={t(decisionLabel(meeting?.approveAgendaStatus))}
            fullWidth
            onClick={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.APPROVE_AGENDA,
                meetingStatus: meeting?.approveAgendaStatus,
              })
            }
            disabled={!isNoticeSentOrLive}
          />
          <Button
            label={t("register-votes-manually")}
            variant={"secondary"}
            onClick={() =>
              controller.setShowGeneralMeetingDialogs(GeneralMeetingVoteType.approveNoticeAndAgenda)
            }
            icon={"table"}
            disabled={!isNoticeSentOrLive}
          />
        </VBox>
        {controller.getShowVotingDialogForType(GeneralMeetingVoteType.approveNoticeAndAgenda) && (
          <VotingDialog
            title={{ key: "documents.generalMeeting.approveAgenda" }}
            projectId={projectId}
            controller={controller}
            user={user}
            meeting={meeting}
            data={data}
            dispatch={dispatch}
            onUpdateVotingStatus={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.APPROVE_AGENDA,
                meetingStatus: meeting?.approveAgendaStatus,
              })
            }
            next={next}
            prev={prev}
          />
        )}

        {controller.votesModel && (
          <VotingOutcomeSettings
            model={controller.votesModel.approveNoticeAndAgenda}
            data={data}
            isDisabled={!isNoticeSentOrLive}
            onChange={onSaveVoting}
          />
        )}
      </VBox>
    );
  },
);

export const Decision: FC<{
  projectId: string;
  decisionId: string;
  title: TMessage;
  meeting: ElectronicGeneralMeeting;
  dispatch: DbotEventDispatcher;
  data: LiveVotingResults;
  controller: AdminElectronicGeneralMeetingController;
  defaultVotingType: GeneralMeetingVotingType;
  isApproved: boolean;
  user: User;
  onSaveVoting: (voting: GeneralMeetingVoting) => void;
  onSaveVotingType: (votingType?: GeneralMeetingVotingType) => void;
  isNoticeSent?: boolean;
  decisionMatters: string[];
  next?: DbotEvent[];
  prev?: DbotEvent[];
}> = observer(
  ({
    projectId,
    decisionId,
    title,
    meeting,
    dispatch,
    data,
    controller,
    defaultVotingType,
    isApproved,
    user,
    onSaveVoting,
    onSaveVotingType,
    isNoticeSent,
    decisionMatters,
    next,
    prev,
  }) => {
    const { t } = useTranslation();
    const isNoticeSentOrLive = isNoticeSent || meeting.status === GeneralMeetingStatus.STARTED;
    const status = meeting?.decisionStatuses.find((s) => s.decisionId === decisionId)?.status;
    return decisionMatters.includes(decisionId) ? (
      <VBox>
        <MatterVotingStatus
          status={status}
          votes={meeting?.votes?.decisions[decisionId]}
          participants={meeting?.participationList}
          defaultVotingType={defaultVotingType}
          isApproved={isApproved}
          votesModel={controller.votesModel?.getVotingForDecision(decisionId)}
          onSaveVotingType={onSaveVotingType}
        />
        <VotingCharts
          data={data}
          votingType={controller.votesModel
            ?.getVotingForDecision(decisionId)
            .selectedVotingType(defaultVotingType)}
        />
        <VBox gap={"md"} marginTop={"md"} marginBottom={"md"}>
          <Button
            icon={status === GeneralMeetingStatus.FINISHED ? "arrow-counter-clockwise" : undefined}
            variant={status === GeneralMeetingStatus.FINISHED ? "secondary" : "primary"}
            label={t(decisionLabel(status))}
            fullWidth
            onClick={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.DECISION,
                meetingStatus: status,
                decisionId,
              })
            }
            disabled={!isNoticeSentOrLive}
          />
          <Button
            label={t("register-votes-manually")}
            variant={"secondary"}
            onClick={() =>
              controller.setShowGeneralMeetingDialogs(GeneralMeetingVoteType.decision, decisionId)
            }
            icon={"table"}
            disabled={!isNoticeSentOrLive}
          />
        </VBox>
        {controller.getShowVotingDialogForType(GeneralMeetingVoteType.decision, decisionId) && (
          <VotingDialog
            title={title}
            projectId={projectId}
            controller={controller}
            user={user}
            meeting={meeting}
            data={data}
            dispatch={dispatch}
            onUpdateVotingStatus={() =>
              controller.updateMeetingStatus({
                statusName: StatusName.DECISION,
                meetingStatus: status,
                decisionId,
              })
            }
            next={next}
            prev={prev}
          />
        )}

        {controller.votesModel && (
          <VotingOutcomeSettings
            model={controller.votesModel.getVotingForDecision(decisionId)}
            data={data}
            isDisabled={!isNoticeSentOrLive}
            onChange={onSaveVoting}
          />
        )}
      </VBox>
    ) : (
      <VBox gap={"lg"}>
        <Box alignItems={"center"}>
          <span>{t("general-meeting.information-matter.text")}</span>
        </Box>
        <Button
          icon={status === GeneralMeetingStatus.FINISHED ? "arrow-counter-clockwise" : undefined}
          variant={status === GeneralMeetingStatus.FINISHED ? "secondary" : "primary"}
          label={informationMatterLabel(status)}
          fullWidth
          onClick={() =>
            controller.updateMeetingStatus({
              statusName: StatusName.DECISION,
              meetingStatus: status,
              decisionId,
            })
          }
          disabled={!isNoticeSentOrLive}
        />
      </VBox>
    );
  },
);

export const MeetingsDialogs = observer(function MeetingsDialogs({
  cancel,
  submit,
  dialogState,
}: {
  submit: () => void;
  cancel: () => void;
  dialogState?: MeetingDialogState;
}) {
  const { t } = useTranslation();
  switch (dialogState?.type) {
    case "settings":
      return (
        <Dialog
          title={t("meetings.settings")}
          cancelLabel={dialogState.form.isReadonly ? t("close") : t("cancel")}
          submitLabel={t("save")}
          onSubmit={dialogState.form.isReadonly ? undefined : submit}
          onCancel={cancel}
          isSubmitting={dialogState.isSubmitting}
        >
          <MeetingSettingsView form={dialogState.form} />
        </Dialog>
      );
    case "start":
      return (
        <Dialog
          title={t("meetings.general-meeting.confirm.start")}
          submitLabel={t("continue")}
          onCancel={cancel}
          onSubmit={submit}
          isSubmitting={dialogState.isSubmitting}
        >
          <MeetingStartView form={dialogState.form} />
        </Dialog>
      );
    case "end":
      return (
        <Dialog
          title={t("meetings.general-meeting.confirm.ending-meeting")}
          submitLabel={t("continue")}
          onCancel={cancel}
          onSubmit={submit}
          isSubmitting={dialogState.isSubmitting}
        >
          <MeetingEndView form={dialogState.form} />
        </Dialog>
      );
    case "announcement":
      return (
        <Dialog
          title={t("meetings.general-meeting.announcement.title")}
          submitLabel={t("send")}
          onCancel={cancel}
          onSubmit={submit}
          isSubmitting={dialogState.isSubmitting}
        >
          <MeetingAnnouncementView form={dialogState.form} />
        </Dialog>
      );
    default:
      return null;
  }
});

export const GeneralMeetingAttendanceDialog = observer(function MeetingsDialogs({
  dialogState,
}: {
  dialogState?: MeetingAttendanceDialogState;
}) {
  switch (dialogState?.type) {
    case "resend-notice":
      return <MeetingResendNoticeDialog form={dialogState.form} />;
    case "register-power-of-attorney":
      return <RegisterPowerOfAttorneyDialog form={dialogState.form} />;
    case "copy-notice-url-to-clipboard":
      return <CopyNoticeUrlForShareholderDialog form={dialogState.form} />;
    default:
      return null;
  }
});

const MeetingSettingsView = observer(({ form }: { form: MeetingSettingsForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"xl"}>
      <VBox>
        <div>{t("meetings.settings.when-meeting-starts")}</div>
        <MCheckboxGroup
          name={"meetingSettingsStart"}
          model={form.meetingSettings}
          disabled={form.isStartOptionsDisabled}
        />
      </VBox>
    </VBox>
  );
});

const MeetingStartView = observer(({ form }: { form: MeetingStartForm }) => {
  const { t } = useTranslation();

  return (
    <VBox gap={"md"}>
      <Markdown value={t("meetings.general-meeting.confirm.how-it-works")} enableLinks />
      <span>{t("meetings.general-meeting.confirm.before-proceeding")}</span>
      <strong>{t("meetings.settings.when-meeting-starts")}</strong>
      <MCheckboxGroup name={"meetingStarting"} model={form.meetingStart} />
      <span>{t("meetings.general-meeting.confirm.rest-assured")}</span>
    </VBox>
  );
});

const MeetingEndView = observer(({ form }: { form: MeetingEndForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <Alert
        type={form.isAllMattersResolved ? "success" : "warning"}
        message={t(
          form.isAllMattersResolved
            ? "meetings.general-meeting.info.allMatterResolved"
            : "meetings.general-meeting.info.resolveAllMatters",
        )}
      />
      <Markdown value={t("meetings.general-meeting.end.description")} enableLinks />
    </VBox>
  );
});

const MeetingAnnouncementView = observer(({ form }: { form: MeetingAnnouncementForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <div>{t("meetings.general-meeting.announcement.message.label")}</div>
      <div>{t("meetings.general-meeting.announcement.message")}</div>
      {form.message.error && <Alert message={form.message.error} type={"error"} />}
      <MarkdownEditor
        placeholder={t("meetings.general-meeting.announcement.message.placeholder")}
        value={form.message.inputValue}
        onChange={form.message.setInputValue}
      />
      <Checkbox
        label={t("meetings.general-meeting.announcement.copyByEmail")}
        checked={form.copyByEmail}
        onChange={(checked) => form.setCopyByEmail(checked.target.checked)}
      />
    </VBox>
  );
});

const CopyNoticeUrlForShareholderDialog = observer(
  ({ form }: { form: CopyNoticeUrlForShareholderForm }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <Dialog
        title={t("general-meeting.copy-participation-link.title", {
          name: form.shareholder.name,
        })}
        submitLabel={t("general-meeting.copy-participation-link.submit")}
        submitIcon={"link"}
        onCancel={form.close}
        onSubmit={() =>
          form.copyToClipboard(
            t("general-meeting.copy-participation-link.text", {
              companyName: form.companyName,
              date: formatDate(locale, form.dateGeneralMeeting),
              shareholderName: form.shareholder.name,
              url: form.url,
            }),
          )
        }
        isSubmitting={isPending(form.result)}
        isSubmitDisabled={!form.canCopy}
      >
        <VBox gap={"md"}>
          <Alert
            type={"warning"}
            message={t("general-meeting.copy-participation-link.warning", {
              shareholderName: form.shareholder.name,
            })}
          />
          <Checkbox
            label={t("general-meeting.copy-participation-link.confirm", {
              shareholderName: form.shareholder.name,
            })}
            onChange={(event) => {
              form.isConfirmed.setValue(event.target.checked);
            }}
            checked={form.isConfirmed.value}
          />
          {isOk(form.result) && (
            <Alert paddingY={"sm"} type={"success"} message={t(form.result.data)} />
          )}
          {isFailure(form.result) && (
            <Alert paddingY={"sm"} type={"error"} message={t(form.result.error)} />
          )}
        </VBox>
      </Dialog>
    );
  },
);

const MeetingResendNoticeDialog = observer(({ form }: { form: MeetingResendNoticeForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("general-meeting.resendNotice.title", {
        name: form.shareholder.name,
      })}
      submitLabel={t("general-meeting.resendNotice.submit")}
      onCancel={form.close}
      onSubmit={form.submit}
      isSubmitting={isPending(form.result)}
      isSubmitDisabled={!form.isValid}
    >
      <VBox gap={"md"}>
        <MTextInput model={form.email} label={t("general-meeting.resendNotice.email.label")} />
        {isOk(form.result) && (
          <Alert paddingY={"sm"} type={"success"} message={t(form.result.data)} />
        )}
        {isFailure(form.result) && (
          <Alert paddingY={"sm"} type={"error"} message={t(form.result.error)} />
        )}
      </VBox>
    </Dialog>
  );
});

const RegisterPowerOfAttorneyDialog: FC<{
  form: RegisterPowerOfAttorneyForm;
}> = observer(({ form }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOk(form.givePoAResult)) {
      dispatch([showSuccess(form.givePoAResult.data, { autoHide: true })]);
      form.close();
    } else if (isFailure(form.givePoAResult)) {
      dispatch([showError(form.givePoAResult.error, { autoHide: true })]);
    }
  }, [dispatch, form.givePoAResult, form]);

  return (
    <Dialog
      title={t("general-meeting.attendance.powerOfAttorney.register")}
      onCancel={form.close}
      submitLabel={t("register")}
      onSubmit={form.submit}
      isSubmitting={isPending(form.givePoAResult)}
      isSubmitDisabled={form.isSendPoADisabled}
    >
      <VBox gap={"lg"}>
        <VBox>
          <span>{t("general-meeting.attendance.powerOfAttorney.from")}</span>
          <Autocomplete
            items={form.allShareholders}
            value={form.selectedShareholder}
            onSelect={form.setSelectedShareholder}
            onClear={() => form.setSelectedShareholder(undefined)}
            idFn={(value) => value?.id.toString()}
            labelFn={(value) => value.name}
          />
        </VBox>
        {form.givenPowerOfAttorney &&
          !isShareholderAttending(form.attendance, form.selectedShareholderId) && (
            <Alert
              message={t("general-meeting.attendance.powerOfAttorney.edit.info", {
                shareholderName: form.selectedShareholder?.name,
                poaName: form.givenPowerOfAttorneyTo,
              })}
              type={"info"}
            />
          )}
        {isShareholderAttending(form.attendance, form.selectedShareholderId) && (
          <Alert
            message={
              <span>
                {t("general-meeting.attendance.powerOfAttorney.edit.error", {
                  shareholderName: form.selectedShareholder?.name,
                })}
                <LinkButton
                  label={t("general-meeting.attendance.reset-attendance")}
                  color={"inherit"}
                  onClick={form.removeAttendance}
                />
              </span>
            }
            type={"error"}
          />
        )}
        <VBox>
          <span>{t("general-meeting.attendance.powerOfAttorney.to")}</span>
          <GivePowerOfAttorneyToRole
            model={form}
            isDisabled={isGivePowerOfAttorneyToRoleDisabled(
              form.attendance,
              form.selectedShareholderId,
            )}
          />
        </VBox>
      </VBox>
    </Dialog>
  );
});

export default AdminElectronicGeneralMeetingInitializer;
